/**
 * Vocabulaire commun aux visages d'une alerte.
 *
 * La pop-in, la notification système et le panneau de la cloche décrivent le même fait ; les
 * laisser composer chacun sa phrase les ferait diverger au premier ajustement — et
 * l'utilisateur qui bascule d'un canal à l'autre lirait deux formulations du même événement.
 *
 * Deux familles d'alertes passent par ici, distinguées par `kind` :
 *
 * | `kind` | Ce qu'elle dit |
 * |---|---|
 * | `metric` (défaut) | un compte sort de son rythme de trafic habituel |
 * | `access` | un compte ne répond plus : accès coupé, connexion expirée |
 *
 * Le défaut est `metric` : les alertes de trafic existaient seules et leurs enregistrements
 * ne portent pas le champ.
 */

import { DASHBOARD_PAR_TYPE } from '../../constants/plateformes';

export const LIBELLES_METRIQUE = { clicks: 'Clics', cost: 'Budget' };

/** Ce qui est arrivé au compte, en clair. */
export const LIBELLES_STATUT_ACCES = {
  ACCESS_REVOKED: 'accès révoqué',
  AUTH_EXPIRED: 'connexion expirée',
  NOT_FOUND: 'compte introuvable',
  UNKNOWN_ERROR: 'erreur inconnue'
};

/** Les régies, écrites comme ailleurs dans l'application. */
export const LIBELLES_REGIE = {
  GOOGLE_ADS: 'Google Ads',
  MICROSOFT_ADS: 'Microsoft Ads',
  FACEBOOK_ADS: 'Meta Ads',
  PINTEREST_ADS: 'Pinterest Ads',
  TIKTOK_ADS: 'TikTok Ads',
  OPENAI_ADS: 'ChatGPT Ads',
  AWIN: 'Awin',
  GOOGLE_ANALYTICS: 'Google Analytics',
  PIANO_ANALYTICS: 'Piano Analytics',
  MATOMO: 'Matomo',
  GOOGLE_MERCHANT_API: 'Google Merchant Center',
  GOOGLE_SEARCH_CONSOLE: 'Google Search Console',
  GOOGLE_TAG_MANAGER: 'Google Tag Manager'
};

export const estAlerteAcces = (alerte) => alerte?.kind === 'access';

export const formatValeur = (metric, value) => (
  metric === 'cost'
    ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0)
    : Math.round(value || 0).toLocaleString('fr-FR')
);

export const formatEcart = (value) => (
  Number.isFinite(value) ? `${value > 0 ? '+' : ''}${Math.round(value)} %` : '—'
);

/** Ancienneté en clair : « il y a 3 h », plus lisible qu'un horodatage dans une liste. */
export const depuis = (date) => {
  const minutes = Math.round((Date.now() - new Date(date).getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const heures = Math.round(minutes / 60);
  if (heures < 24) return `il y a ${heures} h`;
  const jours = Math.round(heures / 24);
  return `il y a ${jours} j`;
};

/** Le titre des canaux : ce qui est arrivé, suivi du compte. */
export const titreAlerte = (alerte) => {
  if (estAlerteAcces(alerte)) return `Compte inaccessible — ${alerte.accountName}`;
  return `${alerte.direction === 'up' ? 'Dépassement' : 'Chute'} — ${alerte.accountName}`;
};

/** Le détail, en une phrase. */
export const corpsAlerte = (alerte) => {
  if (estAlerteAcces(alerte)) {
    const regie = LIBELLES_REGIE[alerte.provider] || alerte.provider;
    const statut = LIBELLES_STATUT_ACCES[alerte.status] || alerte.status;
    // La régie est nommée, sans quoi un identifiant nu n'apprend rien à qui gère plusieurs
    // plateformes pour le même client.
    return `${regie} : ${statut}. Les données de ce compte ne sont plus accessibles.`;
  }
  return `${LIBELLES_METRIQUE[alerte.metric] || alerte.metric} : `
    + `${formatValeur(alerte.metric, alerte.windowValue)} sur 24 h, contre `
    + `${formatValeur(alerte.metric, alerte.baselineValue)} en moyenne `
    + `(${formatEcart(alerte.deviationPercent)}).`;
};

/**
 * La valeur mise en avant à droite d'une carte : l'écart pour le trafic, le statut pour un
 * accès. `null` quand il n'y a rien à mettre en avant, plutôt qu'un tiret décoratif.
 */
export const valeurSaillante = (alerte) => {
  if (estAlerteAcces(alerte)) return LIBELLES_STATUT_ACCES[alerte.status] || alerte.status;
  return formatEcart(alerte.deviationPercent);
};

/**
 * Où mène une alerte, et sous quel intitulé.
 *
 * **Un écart de trafic mène au dashboard du compte** : c'est là qu'on regarde ce qui a bougé.
 * `cible` est renseignée par le serveur — lui seul connaît le type du compte, que la table des
 * alertes ne stocke pas.
 *
 * **Un accès révoqué mène à la bibliothèque**, et non au dashboard : le dashboard d'un compte
 * auquel on n'a plus accès est vide par construction, et n'apprend rien à qui vient justement
 * d'apprendre que l'accès est coupé. C'est dans la bibliothèque que le compte se reconnecte ou
 * se retire. La bibliothèque n'ayant besoin d'aucune cible, une alerte d'accès reste cliquable
 * même quand le compte n'a pas été retrouvé — le cas d'une propriété Search Console, qui n'a de
 * toute façon pas de dashboard.
 *
 * Cette règle vivait dans le panneau de la cloche. La carte en bas à droite dit la même chose
 * que la ligne du panneau : elle doit mener au même endroit, et le recopier les aurait fait
 * diverger au premier ajustement — comme pour les phrases de ce module.
 *
 * `dashboard` est rendu prêt à être passé tel quel à `onOuvrirCompte` : `null` pour un accès,
 * ce que l'appelant interprète comme « la bibliothèque ».
 */
export const destinationAlerte = (alerte) => {
  const acces = estAlerteAcces(alerte);
  const dashboard = alerte?.cible ? DASHBOARD_PAR_TYPE[alerte.cible.type] : null;
  const ouvrable = Boolean(acces || dashboard);

  return {
    acces,
    dashboard: acces ? null : dashboard,
    ouvrable,
    titre: !ouvrable
      ? undefined
      : (acces
        ? `Voir ${alerte.accountName} dans la bibliothèque`
        : `Ouvrir le dashboard de ${alerte.accountName}`)
  };
};
